import React, { createContext, ReactNode, useState } from 'react';
import { Layout, Menu, MenuProps } from 'antd';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import cx from 'classnames';
import { toString, isNil } from 'lodash';

import {
  AssignmentSections,
  AssignmentInfoTypes,
} from 'features/assignment/enums';
import {
  userSelector,
  isAdminSelector,
  isRecruiterCompanySelector,
  isAdminAccessDeniedSelector,
} from 'redux/selectors/authenticationSelectors';
import {
  AssignmentInfoUserResponseModel,
  ProfileTypeEnum,
} from 'api/backofficeApiClient';
import {
  hasCustomers,
  handleClientModalOpen,
} from 'redux/reducers/clientSlice';
import {
  clientDetailsSelector,
  isClientModalOpenSelector,
  isMemberAdditionalInfoVisibleSelector,
} from 'redux/selectors/clientSelectors';
import { UserRole } from 'enums';
import AppHeader from '../AppHeader';
import Accessor from 'components/Accessor';
import { getUserRoles } from 'utils/userRolesUtils';
import { MEDIA_DIMENSIONS_MAP } from 'utils/mediaUtils';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { TeamSectionEnum } from 'features/team/enums';
import { ClientSections } from 'features/client/enums';
import { WorkersSectionsEnum } from 'features/worker/enums';
import ClientModal from 'components/modals/ClientModal';
import PartnerModal from 'components/modals/PartnerModal';
import { disableSiderMenu } from 'utils/urlUtils';
import RecruiterModal from 'components/modals/RecruiterModal';
import { CompanyProfileSection } from 'features/account/enums';
import { TourStepEnum } from 'features/onboarding/enums';
import BaseAlert from 'components/wrappers/BaseAlert';
import WelcomeTour from 'features/onboarding/WelcomeTour';
import WorkerModal from 'components/modals/WorkerModal';
import { AdminPartnerSectionEnum } from 'features/admin/partner/enums';
import { isTourStepActiveSelector } from 'redux/selectors/onboardingSelectors';
import SendNotificationModal from 'components/modals/SendNotificationModal';
import { ClientDetailSection } from 'features/client/ClientDetailsPage/enums';
import { recruiterDetailsSelector } from 'redux/selectors/recruiterSelectors';
import {
  assignmentDetailsSelector,
  isTemplateAssignmentModalOpenSelector,
} from 'redux/selectors/assignmentSelectors';
import {
  checkIfProfileIsCompleted,
  checkIfOnlyBillingInfoUnfilled,
} from 'redux/reducers/accountSlice';
import AssignmentValidationError from 'features/assignment/AssignmentValidationError';
import { handleTemplateAssignmentModalOpen } from 'redux/reducers/assignmentSlice';
import { AdminRequestsSections } from 'features/admin/request/RequestsPage/enums';
import { AdminRecruiterSections } from 'features/admin/member/RecruitersPage/enums';
import { RecruiterDetailSection } from 'features/recruiter/RecruiterDetailsPage/enums';
import { isWorkerModalOpenSelector } from 'redux/selectors/workerSelectors';
import { handleWorkerModalOpen } from 'redux/reducers/workerSlice';
import { ACHPaymentsSections } from 'features/admin/achPayments/enums';
import { IS_PRODUCTION_ENV } from 'constants/env.constants';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import TemplateAssignmentModal from 'components/modals/TemplateAssignmentModal';
import { AccountSettingsSectionEnum } from 'features/account/enums';
import { PartnerSectionEnum } from 'features/partner/enums/PartnerSectionEnum';

import './MainLayout.less';
import styles from './MainLayout.module.less';
import { LoadingOutlined } from '@ant-design/icons';

const { Content, Sider } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

type Props = {
  children: ReactNode;
};

interface MainLayoutContextProps {
  isSidebarCollapsed: boolean;
}

export const MainLayoutContext = createContext<MainLayoutContextProps | null>(
  null,
);

const SIDER_BREAKPOINT = 'md';

const getIsSmallScreen = () => {
  const match = window.matchMedia(
    `(max-width: ${MEDIA_DIMENSIONS_MAP[SIDER_BREAKPOINT]})`,
  );

  return match.matches;
};

const getCollapsed = (): boolean => {
  const isSmallScreen = getIsSmallScreen();

  if (isSmallScreen) return true;

  const storageCollapsed = localStorage.getItem('sidebar-collapsed');

  return !isNil(storageCollapsed) ? JSON.parse(storageCollapsed) : false;
};

const renderIcon = ({
  loading,
  iconClassName,
}: {
  loading?: boolean;
  iconClassName?: string;
}) => {
  return loading ? (
    <LoadingOutlined />
  ) : (
    <span className={cx('icon', iconClassName)} />
  );
};

const getClassNames = ({
  className,
  disabled,
  disableColored,
}: {
  className?: string;
  disabled?: boolean;
  disableColored?: boolean;
}) => {
  return cx(className, {
    // Split coloring and permission to click into two variables due to
    // https://mybasepay.atlassian.net/browse/MBOM-2102
    [styles.disabled]: disabled,
    [styles.disableColored]: disableColored,
  });
};

export default function MainLayout(props: Props) {
  const { children } = props;
  const user = useAppSelector(userSelector);
  const isAdminAccessDenied = useAppSelector(isAdminAccessDeniedSelector);
  const isSidebarTourStepActive = useAppSelector(
    isTourStepActiveSelector(TourStepEnum.Sidebar),
  );
  const isClientModalOpen = useAppSelector(isClientModalOpenSelector);
  const isRecruiterCompany = useAppSelector(isRecruiterCompanySelector);
  const recruiterDetails = useAppSelector(recruiterDetailsSelector);
  const clientDetails = useAppSelector(clientDetailsSelector);
  const isMemberAdditionalInfoVisible = useAppSelector(
    isMemberAdditionalInfoVisibleSelector,
  );
  const assignmentDetails = useAppSelector(assignmentDetailsSelector);
  const isWorkerModalOpen = useAppSelector(isWorkerModalOpenSelector);
  const isTemplateAssignmentModalOpen = useAppSelector(
    isTemplateAssignmentModalOpenSelector,
  );
  const isAdmin = useAppSelector(isAdminSelector);
  const dispatch = useAppDispatch();
  const [collapsed, setCollapsed] = useState(getCollapsed());
  const [isSmallScreen, setIsSmallScreen] = useState(getIsSmallScreen());
  const [isPartnerModalVisible, setIsPartnerModalVisible] = useState(false);
  const [isNewAssignmentCreationChecking, setIsNewAssignmentCreationChecking] =
    useState(false);
  const [isRecruiterModalVisible, setIsRecruiterModalVisible] = useState(false);
  const [isNotificationModalVisible, setIsNotificationModalVisible] =
    useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, search } = location;
  const memberDetailsMatch = useMatch('/member/:id');
  const customerDetailsMatch = useMatch('/customer/:clientId');
  const assignmentInfoMatch = useMatch('/assignment/:id');
  const query = queryString.parse(search);
  const areSiderButtonsDisabled = isNewAssignmentCreationChecking;

  const getSiderTrigger = () => (
    <div onClick={() => handleTriggerClick(!collapsed)}>
      {collapsed ? (
        <span className="icon icon-double-right" />
      ) : (
        <span className="icon icon-double-left" />
      )}
    </div>
  );

  const getSiderWidth = () => {
    if (isSmallScreen) return 276;

    if (!isSmallScreen && !collapsed) return 276;

    return undefined;
  };

  const handleTriggerClick = (collapsed: boolean) => {
    localStorage.setItem('sidebar-collapsed', toString(collapsed));

    setCollapsed(collapsed);
  };

  const handleBreakpoint = (broken: boolean) => {
    setCollapsed(getCollapsed());
    setIsSmallScreen(broken);
  };

  const handleSiderCollapse = (collapsed: boolean) => {
    isSmallScreen && setCollapsed(collapsed);
  };

  const handleNewAssignmentOpen = async () => {
    const validationRequests = isRecruiterCompany
      ? [dispatch(checkIfProfileIsCompleted()), dispatch(hasCustomers())]
      : [
          dispatch(checkIfProfileIsCompleted()),
          dispatch(checkIfOnlyBillingInfoUnfilled()),
        ];

    setIsNewAssignmentCreationChecking(true);

    const [profileStatusResponse, additionalValidationResponse] =
      await Promise.all(validationRequests);

    setIsNewAssignmentCreationChecking(false);

    if (isRecruiterCompany) {
      if (hasCustomers.fulfilled.match(additionalValidationResponse)) {
        if (!additionalValidationResponse.payload) {
          return BaseAlert.error(
            'You need to have a Customer before creating an Assignment.',
          );
        }
      } else {
        return BaseAlert.error(
          additionalValidationResponse.error.message || ApiErrorMessage,
        );
      }
    }

    if (!isRecruiterCompany) {
      if (
        checkIfOnlyBillingInfoUnfilled.fulfilled.match(
          additionalValidationResponse,
        )
      ) {
        if (additionalValidationResponse.payload) {
          return BaseAlert.error(
            <AssignmentValidationError ifOnlyBillingInfoUnfilled />,
          );
        }
      } else {
        return BaseAlert.error(
          additionalValidationResponse.error.message || ApiErrorMessage,
        );
      }
    }

    if (checkIfProfileIsCompleted.fulfilled.match(profileStatusResponse)) {
      if (!profileStatusResponse.payload) {
        return BaseAlert.error(<AssignmentValidationError />);
      }
    } else {
      return BaseAlert.error(
        profileStatusResponse.error.message || ApiErrorMessage,
      );
    }

    setTimeout(() => {
      openTemplateAssignmentModal();
    }, 100);
  };

  const handleNewClientOpen = () => {
    dispatch(handleClientModalOpen(true));
  };

  const closeTemplateAssignmentModal = () => {
    dispatch(handleTemplateAssignmentModalOpen(false));
  };

  const openTemplateAssignmentModal = () => {
    dispatch(handleTemplateAssignmentModalOpen(true));
  };

  const handleNewClientClose = () => {
    dispatch(handleClientModalOpen(false));
  };

  const handleCreateBlogPost = () => {
    window.open(
      `${process.env.REACT_APP_LANDING_URL}/blog/mbpinternal`,
      '_newtab' + Date.now(),
    );
  };

  const getSiderBottomMenu = (): MenuItem[] => {
    const conciergeItems: MenuItem[] = [
      {
        label: 'Concierge',
        key: 'concierge',
        icon: <span className={cx('icon', 'icon-concierge icon-lg')} />,
        className: styles.submenuTour,
        popupClassName: styles.popupTour,
        children: [
          {
            label: 'Product tour',
            key: 'productTour',
            onClick: () => navigate('/tour'),
            icon: renderIcon({
              iconClassName: 'icon-product-tour',
            }),
          },
        ],
      },
    ];

    const groupItems: MenuItem[] = [
      {
        label: 'Dashboard',
        key: 'dashboards',
        onClick: () => navigate('/'),
        className: cx({
          active: pathname === '/',
        }),
        icon: renderIcon({
          iconClassName: 'icon-pie-chart icon-lg',
        }),
      },
      {
        label: 'Company profile',
        key: 'company-profile',
        onClick: () =>
          navigate(
            isAdmin
              ? '/blank-page'
              : `/company-profile?section=${CompanyProfileSection.PrimaryInfo}`,
          ),
        className: cx({
          active: pathname === (isAdmin ? '/blank-page' : '/company-profile'),
        }),
        icon: renderIcon({
          iconClassName: 'icon-user-settings icon-lg',
        }),
      },
    ];

    if (!isAdmin) {
      groupItems.push(...conciergeItems);
    }

    return [
      {
        type: 'group',
        children: groupItems,
      },
    ];
  };

  const getItemsForRecruiterCompany = (): MenuProps['items'] => {
    const items: MenuItem[] = [
      {
        label: 'Create new customer',
        key: 'new-client',
        onClick: handleNewClientOpen,
        disabled: areSiderButtonsDisabled,
        className: getClassNames({ disabled: areSiderButtonsDisabled }),
        icon: renderIcon({ iconClassName: 'icon-new-clients' }),
      },
      {
        label: 'Create new assignment',
        key: 'new-assignment',
        onClick: handleNewAssignmentOpen,
        disabled: areSiderButtonsDisabled,
        className: getClassNames({
          disabled: areSiderButtonsDisabled,
          disableColored: isNewAssignmentCreationChecking,
        }),
        icon: renderIcon({
          iconClassName: 'icon-assignmant',
          loading: isNewAssignmentCreationChecking,
        }),
      },
      {
        label: 'Create new worker',
        key: 'new-worker',
        onClick: () => dispatch(handleWorkerModalOpen(true)),
        disabled: areSiderButtonsDisabled,
        className: getClassNames({
          disabled: areSiderButtonsDisabled,
        }),
        icon: renderIcon({
          iconClassName: 'icon-worker',
        }),
      },
      {
        label: 'Create new partner',
        key: 'new-partner',
        onClick: () => setIsPartnerModalVisible(true),
        disabled: areSiderButtonsDisabled,
        className: getClassNames({
          disabled: areSiderButtonsDisabled,
        }),
        icon: renderIcon({
          iconClassName: 'icon-new-partner',
        }),
      },
    ];

    if (pathname === '/account-settings') {
      items.push({
        type: 'group',
        children: [
          {
            label: 'Notification Settings',
            key: 'notificationSection',
            onClick: () =>
              navigate(
                `/account-settings?section=${AccountSettingsSectionEnum.Notification}`,
              ),
            className: cx({
              active: query.section === AccountSettingsSectionEnum.Notification,
            }),
            icon: renderIcon({
              iconClassName: 'icon-active-assignment',
            }),
          },
          {
            label: 'Security',
            key: 'securitySection',
            onClick: () =>
              navigate(
                `/account-settings?section=${AccountSettingsSectionEnum.Security}`,
              ),
            className: cx({
              active: query.section === AccountSettingsSectionEnum.Security,
            }),
            icon: renderIcon({ iconClassName: 'icon-security' }),
          },
        ],
      });
    }

    if (pathname === '/customers') {
      items.push({
        type: 'group',
        children: [
          {
            label: 'Approved customers',
            key: 'recruiterClients',
            onClick: () =>
              navigate(`/customers?section=${ClientSections.Approved}`),
            className: cx({
              active: query.section === ClientSections.Approved,
            }),
            icon: renderIcon({
              iconClassName: 'icon-approved-customers',
            }),
          },
          {
            label: 'Pending customers',
            key: 'clientsPending',
            onClick: () =>
              navigate(`/customers?section=${ClientSections.Pending}`),
            className: cx({
              active: query.section === ClientSections.Pending,
            }),
            icon: renderIcon({
              iconClassName: 'icon-pending-customers',
            }),
          },
          {
            label: 'Drafts',
            key: 'clientsDrafts',
            onClick: () =>
              navigate(`/customers?section=${ClientSections.Draft}`),
            className: cx({
              active: query.section === ClientSections.Draft,
            }),
            icon: renderIcon({
              iconClassName: 'icon-draft-customers',
            }),
          },
        ],
      });
    }

    if (customerDetailsMatch && !!clientDetails) {
      const groupItems: MenuItem[] = [
        {
          label: 'Primary information',
          key: 'memberCustomerPrimaryInfoSidebarLink',
          onClick: () =>
            navigate(`${pathname}?section=${ClientDetailSection.PrimaryInfo}`),
          className: cx({
            active: query.section === ClientDetailSection.PrimaryInfo,
          }),
          icon: renderIcon({
            iconClassName: 'icon-primary-info',
          }),
        },
      ];

      if (isMemberAdditionalInfoVisible === true) {
        groupItems.push({
          label: 'Additional information',
          key: 'memberCustomerAdditionalInfoSidebarLink',
          onClick: () =>
            navigate(
              `${pathname}?section=${ClientDetailSection.AdditionalInfo}`,
            ),
          className: cx({
            active: query.section === ClientDetailSection.AdditionalInfo,
          }),
          icon: renderIcon({
            iconClassName: 'icon-additional-info',
          }),
        });
      }

      items.push({
        type: 'group',
        children: groupItems,
      });
    }

    if (pathname === '/assignments') {
      items.push({
        type: 'group',
        children: [
          {
            label: 'My assignments',
            key: 'recruiterMyAssignments',
            onClick: () =>
              navigate(
                `/assignments?section=${AssignmentSections.AllAssignments}`,
              ),
            className: cx({
              active: query.section === AssignmentSections.AllAssignments,
            }),
            icon: renderIcon({
              iconClassName: 'icon-active-assignment',
            }),
          },
          {
            label: 'Drafts',
            key: 'assignments-drafts',
            onClick: () =>
              navigate(`/assignments?section=${AssignmentSections.Drafts}`),
            className: cx({
              active: query.section === AssignmentSections.Drafts,
            }),
            icon: renderIcon({
              iconClassName: 'icon-draft-assignment',
            }),
          },
          {
            label: 'Templates',
            key: 'assignments-templates',
            onClick: () =>
              navigate(`/assignments?section=${AssignmentSections.Templates}`),
            className: cx({
              active: query.section === AssignmentSections.Templates,
            }),
            icon: renderIcon({
              iconClassName: 'icon-templates',
            }),
          },
          {
            label: 'Partner assignments',
            key: 'assignments-partner',
            onClick: () =>
              navigate(`/assignments?section=${AssignmentSections.Partner}`),
            className: cx({
              active: query.section === AssignmentSections.Partner,
            }),
            icon: renderIcon({
              iconClassName: 'icon-partner-assignment',
            }),
          },
        ],
      });
    }

    if (assignmentInfoMatch && !!assignmentDetails) {
      const groupItems = [
        {
          label: 'Primary information',
          key: 'memberPrimaryAssignmentInfo',
          onClick: () =>
            navigate(`${pathname}?section=${AssignmentInfoTypes.Primary}`),
          className: cx({
            active: query.section === AssignmentInfoTypes.Primary,
          }),
          icon: renderIcon({
            iconClassName: 'icon-primary-info',
          }),
        },
      ];

      if (
        (assignmentDetails as AssignmentInfoUserResponseModel)
          ?.isAdditionalInfoExists
      ) {
        groupItems.push({
          label: 'Additional information',
          key: 'memberAdditionalAssignmentInfo',
          onClick: () =>
            navigate(`${pathname}?section=${AssignmentInfoTypes.Additional}`),
          className: cx({
            active: query.section === AssignmentInfoTypes.Additional,
          }),
          icon: renderIcon({
            iconClassName: 'icon-additional-info',
          }),
        });
      }

      items.push({
        type: 'group',
        children: groupItems,
      });
    }

    if (pathname === '/team') {
      const groupItems = [
        {
          label: 'Members',
          key: 'recruiters',
          onClick: () =>
            navigate(`/team?section=${TeamSectionEnum.Recruiters}`),
          className: cx({
            active: query.section === TeamSectionEnum.Recruiters,
          }),
          icon: renderIcon({
            iconClassName: 'icon-members',
          }),
        },
      ];
      items.push({
        type: 'group',
        children: groupItems,
      });
    }

    if (pathname === '/workers') {
      const groupItems = [
        {
          label: 'Workers',
          key: 'workers',
          onClick: () =>
            navigate(`/workers?section=${WorkersSectionsEnum.Workers}`),
          className: cx({
            active: query.section === WorkersSectionsEnum.Workers,
          }),
          icon: renderIcon({
            iconClassName: 'icon-workers',
          }),
        },
        {
          label: 'Contractor companies',
          key: 'contractorCompanies',
          onClick: () =>
            navigate(
              `/workers?section=${WorkersSectionsEnum.ContractorCompanies}`,
            ),
          className: cx({
            active: query.section === WorkersSectionsEnum.ContractorCompanies,
          }),
          icon: renderIcon({
            iconClassName: 'icon-contractor-companies',
          }),
        },
      ];

      items.push({
        type: 'group',
        children: groupItems,
      });
    }

    if (pathname === '/partners') {
      items.push({
        type: 'group',
        children: [
          {
            label: 'Partners ready for payments',
            key: 'partnersReadyForPayments',
            onClick: () =>
              navigate(
                `/partners?section=${PartnerSectionEnum.readyForPayments}`,
              ),
            className: cx({
              active: query.section === PartnerSectionEnum.readyForPayments,
            }),
            icon: renderIcon({
              iconClassName: 'icon-payment-partners',
            }),
          },
          {
            label: 'Pending partners',
            key: 'pendingPartners',
            onClick: () =>
              navigate(`/partners?section=${PartnerSectionEnum.pending}`),
            className: cx({
              active: query.section === PartnerSectionEnum.pending,
            }),
            icon: renderIcon({
              iconClassName: 'icon-pending-partners',
            }),
          },
        ],
      });
    }

    return items;
  };

  const getItemsForCorporateTA = (): MenuProps['items'] => {
    const items: MenuItem[] = [
      {
        label: 'Create new assignment',
        key: 'new-assignment',
        onClick: handleNewAssignmentOpen,
        disabled: areSiderButtonsDisabled,
        className: getClassNames({
          disabled: areSiderButtonsDisabled,
          disableColored: isNewAssignmentCreationChecking,
        }),
        icon: renderIcon({
          iconClassName: 'icon-assignmant',
          loading: isNewAssignmentCreationChecking,
        }),
      },
      {
        label: 'Create new worker',
        key: 'new-worker',
        onClick: () => dispatch(handleWorkerModalOpen(true)),
        disabled: areSiderButtonsDisabled,
        className: getClassNames({
          disabled: areSiderButtonsDisabled,
        }),
        icon: renderIcon({
          iconClassName: 'icon-worker',
        }),
      },
      {
        label: 'Create new partner',
        key: 'new-partner',
        onClick: () => setIsPartnerModalVisible(true),
        disabled: areSiderButtonsDisabled,
        className: getClassNames({
          disabled: areSiderButtonsDisabled,
        }),
        icon: renderIcon({
          iconClassName: 'icon-new-partner',
        }),
      },
    ];

    if (pathname === '/account-settings') {
      items.push({
        type: 'group',
        children: [
          {
            label: 'Notification Settings',
            key: 'notificationSection',
            onClick: () =>
              navigate(
                `/account-settings?section=${AccountSettingsSectionEnum.Notification}`,
              ),
            className: cx({
              active: query.section === AccountSettingsSectionEnum.Notification,
            }),
            icon: renderIcon({
              iconClassName: 'icon-active-assignment',
            }),
          },
          {
            label: 'Security',
            key: 'securitySection',
            onClick: () =>
              navigate(
                `/account-settings?section=${AccountSettingsSectionEnum.Security}`,
              ),
            className: cx({
              active: query.section === AccountSettingsSectionEnum.Security,
            }),
            icon: renderIcon({ iconClassName: 'icon-security' }),
          },
        ],
      });
    }

    if (pathname === '/assignments') {
      items.push({
        type: 'group',
        children: [
          {
            label: 'My assignments',
            key: 'recruiterMyAssignments',
            onClick: () =>
              navigate(
                `/assignments?section=${AssignmentSections.AllAssignments}`,
              ),
            className: cx({
              active: query.section === AssignmentSections.AllAssignments,
            }),
            icon: renderIcon({
              iconClassName: 'icon-active-assignment',
            }),
          },
          {
            label: 'Drafts',
            key: 'assignments-drafts',
            onClick: () =>
              navigate(`/assignments?section=${AssignmentSections.Drafts}`),
            className: cx({
              active: query.section === AssignmentSections.Drafts,
            }),
            icon: renderIcon({
              iconClassName: 'icon-draft-assignment',
            }),
          },
          {
            label: 'Templates',
            key: 'assignments-templates',
            onClick: () =>
              navigate(`/assignments?section=${AssignmentSections.Templates}`),
            className: cx({
              active: query.section === AssignmentSections.Templates,
            }),
            icon: renderIcon({
              iconClassName: 'icon-templates',
            }),
          },
        ],
      });
    }

    if (assignmentInfoMatch && !!assignmentDetails) {
      const groupItems = [
        {
          label: 'Primary information',
          key: 'memberPrimaryAssignmentInfo',
          onClick: () =>
            navigate(`${pathname}?section=${AssignmentInfoTypes.Primary}`),
          className: cx({
            active: query.section === AssignmentInfoTypes.Primary,
          }),
          icon: renderIcon({
            iconClassName: 'icon-primary-info',
          }),
        },
      ];

      if (
        (assignmentDetails as AssignmentInfoUserResponseModel)
          ?.isAdditionalInfoExists
      ) {
        groupItems.push({
          label: 'Additional information',
          key: 'memberAdditionalAssignmentInfo',
          onClick: () =>
            navigate(`${pathname}?section=${AssignmentInfoTypes.Additional}`),
          className: cx({
            active: query.section === AssignmentInfoTypes.Additional,
          }),
          icon: renderIcon({
            iconClassName: 'icon-additional-info',
          }),
        });
      }

      items.push({
        type: 'group',
        children: groupItems,
      });
    }

    if (pathname === '/team') {
      const groupItems = [
        {
          label: 'Members',
          key: 'recruiters',
          onClick: () =>
            navigate(`/team?section=${TeamSectionEnum.Recruiters}`),
          className: cx({
            active: query.section === TeamSectionEnum.Recruiters,
          }),
          icon: renderIcon({
            iconClassName: 'icon-members',
          }),
        },
      ];
      items.push({
        type: 'group',
        children: groupItems,
      });
    }

    if (pathname === '/workers') {
      const groupItems = [
        {
          label: 'Workers',
          key: 'workers',
          onClick: () =>
            navigate(`/workers?section=${WorkersSectionsEnum.Workers}`),
          className: cx({
            active: query.section === WorkersSectionsEnum.Workers,
          }),
          icon: renderIcon({
            iconClassName: 'icon-workers',
          }),
        },
        {
          label: 'Contractor companies',
          key: 'contractorCompanies',
          onClick: () =>
            navigate(
              `/workers?section=${WorkersSectionsEnum.ContractorCompanies}`,
            ),
          className: cx({
            active: query.section === WorkersSectionsEnum.ContractorCompanies,
          }),
          icon: renderIcon({
            iconClassName: 'icon-contractor-companies',
          }),
        },
      ];

      items.push({
        type: 'group',
        children: groupItems,
      });
    }

    if (pathname === '/company-profile') {
      const groupItems = [
        {
          label: 'Primary information',
          key: 'companyProfilePrimaryInfo',
          onClick: () =>
            navigate(
              `/company-profile?section=${CompanyProfileSection.PrimaryInfo}`,
            ),
          className: cx({
            active: query.section === CompanyProfileSection.PrimaryInfo,
          }),
          icon: renderIcon({
            iconClassName: 'icon-primary-info',
          }),
        },
        {
          label: 'Additional information',
          key: 'companyProfileAdditionalInfo',
          onClick: () =>
            navigate(
              `/company-profile?section=${CompanyProfileSection.AdditionalInfo}`,
            ),
          className: cx({
            active: query.section === CompanyProfileSection.AdditionalInfo,
          }),
          icon: renderIcon({
            iconClassName: 'icon-additional-info',
          }),
        },
      ];

      items.push({
        type: 'group',
        children: groupItems,
      });
    }

    return items;
  };

  const getItemsForAdmin = (): MenuProps['items'] => {
    const items: MenuItem[] = [
      {
        label: 'Send notification',
        key: 'send-notification',
        onClick: () => setIsNotificationModalVisible(true),
        icon: renderIcon({
          iconClassName: 'icon-sending-letter',
        }),
      },
    ];

    if (IS_PRODUCTION_ENV) {
      items.push({
        label: 'Create a Blog Post',
        key: 'create-blog-post',
        onClick: handleCreateBlogPost,
        icon: renderIcon({
          iconClassName: 'icon-blog',
        }),
      });
    }

    items.push({
      label: 'Timesheet Status Report',
      key: 'timesheet-status-report',
      onClick: () => navigate('/timesheet-status-report-details'),
      icon: renderIcon({
        iconClassName: 'icon-timesheet',
      }),
    });

    if (pathname === '/account-settings') {
      items.push({
        type: 'group',
        children: [
          {
            label: 'Security',
            key: 'securitySection',
            onClick: () =>
              navigate(
                `/account-settings?section=${AccountSettingsSectionEnum.Security}`,
              ),
            className: cx({
              active: query.section === AccountSettingsSectionEnum.Security,
            }),
            icon: renderIcon({ iconClassName: 'icon-security' }),
          },
        ],
      });
    }

    if (pathname === '/todo') {
      items.push({
        type: 'group',
        children: [
          {
            label: 'Members',
            key: 'membersRequests',
            onClick: () =>
              navigate(`/todo?section=${AdminRequestsSections.Members}`),
            className: cx({
              active: query.section === AdminRequestsSections.Members,
            }),
            icon: renderIcon({
              iconClassName: 'icon-members',
            }),
          },
          {
            label: 'Customers',
            key: 'clientsRequests',
            onClick: () =>
              navigate(`/todo?section=${AdminRequestsSections.Clients}`),
            className: cx({
              active: query.section === AdminRequestsSections.Clients,
            }),
            icon: renderIcon({
              iconClassName: 'icon-customers',
            }),
          },
          {
            label: 'Assignments',
            key: 'assignmentsRequests',
            onClick: () =>
              navigate(`/todo?section=${AdminRequestsSections.Assignments}`),
            className: cx({
              active: query.section === AdminRequestsSections.Assignments,
            }),
            icon: renderIcon({
              iconClassName: 'icon-active-assignment',
            }),
          },
          {
            label: 'Workers',
            key: 'workersRequests',
            onClick: () =>
              navigate(`/todo?section=${AdminRequestsSections.Workers}`),
            className: cx({
              active: query.section === AdminRequestsSections.Workers,
            }),
            icon: renderIcon({
              iconClassName: 'icon-workers',
            }),
          },
        ],
      });
    }

    if (pathname === '/members') {
      items.push({
        type: 'group',
        children: [
          {
            label: 'Members',
            key: 'adminRecruiters',
            onClick: () =>
              navigate(`/members?section=${AdminRecruiterSections.Recruiters}`),
            className: cx({
              active: query.section === AdminRecruiterSections.Recruiters,
            }),
            icon: renderIcon({
              iconClassName: 'icon-members',
            }),
          },
        ],
      });
    }

    if (
      recruiterDetails?.profileType === ProfileTypeEnum.Corporate &&
      memberDetailsMatch
    ) {
      items.push({
        type: 'group',
        children: [
          {
            label: 'Primary information',
            key: 'adminRecruiterPrimaryInfoSidebarLink',
            onClick: () =>
              navigate(
                `${pathname}?section=${RecruiterDetailSection.PrimaryInfo}`,
              ),
            className: cx({
              active: query.section === RecruiterDetailSection.PrimaryInfo,
            }),
            icon: renderIcon({
              iconClassName: 'icon-primary-info',
            }),
          },
          {
            label: 'Additional information',
            key: 'adminRecruiterAdditionalInfoSidebarLink',
            onClick: () =>
              navigate(
                `${pathname}?section=${RecruiterDetailSection.AdditionalInfo}`,
              ),
            className: cx({
              active: query.section === RecruiterDetailSection.AdditionalInfo,
            }),
            icon: renderIcon({
              iconClassName: 'icon-additional-info',
            }),
          },
        ],
      });
    }

    if (pathname === '/customers') {
      items.push({
        type: 'group',
        children: [
          {
            label: 'Approved customers',
            key: 'adminApprovedClients',
            onClick: () =>
              navigate(`/customers?section=${ClientSections.Approved}`),
            className: cx({
              active: query.section === ClientSections.Approved,
            }),
            icon: renderIcon({
              iconClassName: 'icon-approved-customers',
            }),
          },
        ],
      });
    }

    if (customerDetailsMatch) {
      items.push({
        type: 'group',
        children: [
          {
            label: 'Primary information',
            key: 'adminCustomerPrimaryInfoSidebarLink',
            onClick: () =>
              navigate(
                `${pathname}?section=${ClientDetailSection.PrimaryInfo}`,
              ),
            className: cx({
              active: query.section === ClientDetailSection.PrimaryInfo,
            }),
            icon: renderIcon({
              iconClassName: 'icon-primary-info',
            }),
          },
          {
            label: 'Additional information',
            key: 'adminCustomerAdditionalInfoSidebarLink',
            onClick: () =>
              navigate(
                `${pathname}?section=${ClientDetailSection.AdditionalInfo}`,
              ),
            className: cx({
              active: query.section === ClientDetailSection.AdditionalInfo,
            }),
            icon: renderIcon({
              iconClassName: 'icon-additional-info',
            }),
          },
        ],
      });
    }

    if (pathname === '/assignments') {
      items.push({
        type: 'group',
        children: [
          {
            label: 'All assignments',
            key: 'adminAllAssignments',
            onClick: () =>
              navigate(
                `/assignments?section=${AssignmentSections.AllAssignments}`,
              ),
            className: cx({
              active: query.section === AssignmentSections.AllAssignments,
            }),
            icon: renderIcon({
              iconClassName: 'icon-active-assignment',
            }),
          },
        ],
      });
    }

    if (assignmentInfoMatch) {
      items.push({
        type: 'group',
        children: [
          {
            label: 'Primary information',
            key: 'adminPrimaryAssignmentInfo',
            onClick: () =>
              navigate(`${pathname}?section=${AssignmentInfoTypes.Primary}`),
            className: cx({
              active: query.section === AssignmentInfoTypes.Primary,
            }),
            icon: renderIcon({
              iconClassName: 'icon-primary-info',
            }),
          },
          {
            label: 'Additional information',
            key: 'adminAdditionalAssignmentInfo',
            onClick: () =>
              navigate(`${pathname}?section=${AssignmentInfoTypes.Additional}`),
            className: cx({
              active: query.section === AssignmentInfoTypes.Additional,
            }),
            icon: renderIcon({
              iconClassName: 'icon-additional-info',
            }),
          },
        ],
      });
    }

    if (pathname === '/partners') {
      items.push({
        type: 'group',
        children: [
          {
            label: 'Partners in TC',
            key: 'adminPartnersWithMP',
            onClick: () =>
              navigate(`/partners?section=${AdminPartnerSectionEnum.withMP}`),
            className: cx({
              active: query.section === AdminPartnerSectionEnum.withMP,
            }),
            icon: renderIcon({
              iconClassName: 'icon-folk',
            }),
          },
          {
            label: 'Partners not in TC',
            key: 'adminPartnersWithoutMP',
            onClick: () =>
              navigate(
                `/partners?section=${AdminPartnerSectionEnum.withoutMP}`,
              ),
            className: cx({
              active: query.section === AdminPartnerSectionEnum.withoutMP,
            }),
            icon: renderIcon({
              iconClassName: 'icon-man',
            }),
          },
        ],
      });
    }

    if (pathname === '/workers') {
      items.push({
        type: 'group',
        children: [
          {
            label: 'Approved workers',
            key: 'approvedWorkers',
            onClick: () =>
              navigate(`/workers?section=${WorkersSectionsEnum.Approved}`),
            className: cx({
              active: query.section === WorkersSectionsEnum.Approved,
            }),
            icon: renderIcon({
              iconClassName: 'icon-approved-workers',
            }),
          },
          {
            label: 'Contractor companies',
            key: 'contractorCompanies',
            onClick: () =>
              navigate(
                `/workers?section=${WorkersSectionsEnum.ContractorCompanies}`,
              ),
            className: cx({
              active: query.section === WorkersSectionsEnum.ContractorCompanies,
            }),
            icon: renderIcon({
              iconClassName: 'icon-contractor-companies',
            }),
          },
        ],
      });
    }

    if (pathname === '/ach-payments') {
      items.push({
        type: 'group',
        children: [
          {
            label: 'Unprocessed payments',
            key: 'unprocessedPayments',
            onClick: () =>
              navigate(
                `/ach-payments?section=${ACHPaymentsSections.UnprocessedPayments}`,
              ),
            className: cx({
              active: query.section === ACHPaymentsSections.UnprocessedPayments,
            }),
            icon: renderIcon({
              iconClassName: 'icon-payments',
            }),
          },
        ],
      });
    }

    return items;
  };

  return (
    <MainLayoutContext.Provider value={{ isSidebarCollapsed: collapsed }}>
      <WelcomeTour />

      <Layout className="layout">
        <AppHeader />

        <Layout className={styles.layout}>
          {/* Hide sidebar if user is on access denied page or not found page */}
          {!isAdminAccessDenied && user && !disableSiderMenu(pathname) && (
            <Sider
              trigger={getSiderTrigger()}
              collapsible={!isSmallScreen}
              width={getSiderWidth()}
              className={cx('side-nav', styles.sider, {
                mobile: isSmallScreen,
                'onboarding-spotlight': isSidebarTourStepActive,
              })}
              onBreakpoint={handleBreakpoint}
              breakpoint={SIDER_BREAKPOINT}
              collapsed={collapsed}
              collapsedWidth={isSmallScreen ? 0 : 64}
              onCollapse={isSmallScreen ? handleSiderCollapse : undefined}
            >
              <Accessor roles={getUserRoles(UserRole.RecruiterCompany)}>
                {isTemplateAssignmentModalOpen && (
                  <TemplateAssignmentModal
                    onClose={closeTemplateAssignmentModal}
                  />
                )}

                {isClientModalOpen && (
                  <ClientModal onClose={handleNewClientClose} />
                )}

                {isWorkerModalOpen && (
                  <WorkerModal
                    onClose={() => dispatch(handleWorkerModalOpen(false))}
                  />
                )}

                {isPartnerModalVisible && (
                  <PartnerModal
                    visible={isPartnerModalVisible}
                    onClose={() => setIsPartnerModalVisible(false)}
                  />
                )}

                {isRecruiterModalVisible && (
                  <RecruiterModal
                    visible={isRecruiterModalVisible}
                    onClose={() => setIsRecruiterModalVisible(false)}
                  />
                )}

                <div className={styles.sidebar}>
                  <Menu
                    selectable={false}
                    items={getItemsForRecruiterCompany()}
                  />
                  <Menu
                    selectable={false}
                    items={getSiderBottomMenu()}
                    className={styles.bottomMenu}
                  />
                </div>
              </Accessor>
              <Accessor roles={getUserRoles(UserRole.CorporateTA)}>
                {isTemplateAssignmentModalOpen && (
                  <TemplateAssignmentModal
                    onClose={closeTemplateAssignmentModal}
                  />
                )}

                {isWorkerModalOpen && (
                  <WorkerModal
                    onClose={() => dispatch(handleWorkerModalOpen(false))}
                  />
                )}

                {isPartnerModalVisible && (
                  <PartnerModal
                    visible={isPartnerModalVisible}
                    onClose={() => setIsPartnerModalVisible(false)}
                  />
                )}

                {isRecruiterModalVisible && (
                  <RecruiterModal
                    visible={isRecruiterModalVisible}
                    onClose={() => setIsRecruiterModalVisible(false)}
                  />
                )}

                <div className={styles.sidebar}>
                  <Menu selectable={false} items={getItemsForCorporateTA()} />
                  <Menu
                    selectable={false}
                    items={getSiderBottomMenu()}
                    className={styles.bottomMenu}
                  />
                </div>
              </Accessor>

              <Accessor roles={getUserRoles(UserRole.Admin)}>
                {isNotificationModalVisible && (
                  <SendNotificationModal
                    onClose={() => setIsNotificationModalVisible(false)}
                  />
                )}

                <div className={styles.sidebar}>
                  <Menu selectable={false} items={getItemsForAdmin()} />
                  <Menu
                    selectable={false}
                    items={getSiderBottomMenu()}
                    className={styles.bottomMenu}
                  />
                </div>
              </Accessor>
            </Sider>
          )}

          <Content className={styles.content}>{children}</Content>
        </Layout>
      </Layout>
    </MainLayoutContext.Provider>
  );
}
